/**
 * ArtifactPill — a compact "Artifact — {name}" chip rendered under an
 * assistant message when the SSE stream produced an `artifact` event.
 *
 * Clicking the chip opens the artifact side panel (via `onOpen`). Inline
 * preview was removed — the panel is the single reader.
 */

import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { MessageArtifact } from '@ideoz/core';
import { composeArtifactTitle } from '@ideoz/core';
import { cn } from '../../lib/utils';
import {
  getArtifactFileIcon,
  getArtifactFormatLabel,
  isDownloadableFileFormat,
} from '../../lib/artifactFormat';
import Icon from './Icon';

export interface ArtifactPillProps {
  artifact: MessageArtifact;
  /** Called when the user asks for the side-panel view. */
  onOpen?: (artifact: MessageArtifact) => void;
  /** RTL from the parent message. */
  isRtl?: boolean;
  /** Extra classes for positioning inside MessageItem. */
  className?: string;
}

const humanise = (raw: string): string => {
  const trimmed = raw.replace(/[_-]+/g, ' ').trim();
  if (!trimmed) return raw;
  return trimmed
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Prefer the typed title block ("artifact_name - artifact_type - version")
 * from the structured JSON artifact. Otherwise fall back to the first markdown
 * heading in the content (the AI writes the "real" name there, e.g.
 * "# Renovation Permit Issuance Service Journey"), then the humanised name.
 */
const resolveTitle = (artifact: MessageArtifact): string => {
  const composed = composeArtifactTitle(artifact.structuredContent);
  if (composed) return composed;
  const content = artifact.content ?? '';
  const match = content.match(/^\s*#{1,6}\s+(.+?)\s*$/m);
  if (match?.[1]) return match[1].trim();
  return humanise(artifact.name || 'Artifact');
};

export const ArtifactPill: React.FC<ArtifactPillProps> = ({
  artifact,
  onOpen,
  isRtl,
  className,
}) => {
  const title = resolveTitle(artifact);
  // Surface the real file format (PDF, DOCX, XLSX, …) as a badge + a
  // format-specific icon. Markdown artifacts carry no `fileFormat` (or report
  // `markdown`) — they keep the generic document icon and no badge.
  const showFormat = isDownloadableFileFormat(artifact.fileFormat);
  const FileIcon = getArtifactFileIcon(artifact.fileFormat);

  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border border-gray-200 bg-white w-full',
        'dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      data-testid="artifact-pill"
    >
      <button
        type="button"
        onClick={() => onOpen?.(artifact)}
        className={cn(
          'flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-200 w-full px-6 py-6',
          onOpen && 'hover:text-primary-600 dark:hover:text-primary-400'
        )}
        data-testid="artifact-pill-open"
      >
        <Icon
          icon={FileIcon}
          size={32}
          strokeWidth={1}
          className="shrink-0 stroke-gray-400 dark:stroke-gray-500"
        />
        <div className="flex flex-col">
          {title}
          <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
            Artifact
            {showFormat && (
              <span
                className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                data-testid="artifact-pill-format"
              >
                {getArtifactFormatLabel(artifact.fileFormat)}
              </span>
            )}
          </span>
        </div>
      </button>
    </div>
  );
};

export default ArtifactPill;
